import * as React from 'react';

import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { useRouter } from 'js/lib/useRouter';

import ProductSlug from 'bundles/enterprise-legacy-learner-home/queries/ProductSlugQuery.graphql';
import type {
  ProductSlugQuery,
  ProductSlugQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/ProductSlugQuery';
import { CourseTypeMetadataByCourseIdQuery } from 'bundles/enterprise-legacy-learner-home/utils/ProgramHomeGraphqlQueries';
import type {
  CourseTypeMetadataByCourseIdQuery as CourseTypeMetadataByCourseIdQueryType,
  CourseTypeMetadataByCourseIdQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/utils/__generated__/CourseTypeMetadataByCourseIdQuery';
import { useRouterParams } from 'bundles/enterprise-legacy-xdp/components/ProgramMiniModal';
import MiniModalRedirectContext from 'bundles/unified-home-common/contexts/enterprise/MiniModalRedirectContext';
import CoursesSlug from 'bundles/unified-home-common/queries/CoursesSlugQuery.graphql';
import type {
  CoursesSlugQuery,
  CoursesSlugQueryVariables,
} from 'bundles/unified-home-common/queries/__generated__/CoursesSlugQuery';

// Fetches information when URL contains query params related to the legacy mini modal.
// We may redirect the learner to the XDP in this scenario.
const MiniModalRedirectProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const router = useRouter();
  // Note: useRouterParams is not a Hook, despite the name.
  const routerParams = useRouterParams(router);
  const shouldRunCourseQuery = routerParams?.productType === 'course';
  const {
    loading: isCourseSlugLoading,
    data: courseSlugData,
    error: courseSlugError,
  } = useQuery<CoursesSlugQuery, CoursesSlugQueryVariables>(CoursesSlug, {
    variables: {
      // @ts-expect-error Checked falsiness in skip.
      id: routerParams?.productId,
    },
    context: { clientName: 'gatewayGql' },
    skip: !shouldRunCourseQuery || !routerParams?.productId,
  });
  const course = courseSlugData?.Course?.queryById;
  const {
    loading: isSlugLoading,
    data: slugData,
    error: slugError,
  } = useQuery<ProductSlugQuery, ProductSlugQueryVariables>(ProductSlug, {
    variables: {
      // @ts-expect-error Checked falsiness in skip.
      id: routerParams?.productId,
    },
    skip: shouldRunCourseQuery || !routerParams?.productId,
    context: {
      clientName: 'gatewayGql',
    },
  });
  const s12n = slugData?.Specialization?.queryById;
  const {
    data: projectData,
    loading: isProjectLoading,
    error: projectError,
  } = useQuery<CourseTypeMetadataByCourseIdQueryType, CourseTypeMetadataByCourseIdQueryVariables>(
    CourseTypeMetadataByCourseIdQuery,
    // We confirm courseId is defined from the skip property.
    // @ts-expect-error Checked falsiness in skip.
    { variables: { courseId: course?.id }, skip: !course?.id }
  );
  const isProject =
    course &&
    !isProjectLoading &&
    projectData?.CourseTypeMetadataV1Resource?.get?.courseTypeMetadata.__typename !==
      'CourseTypeMetadataV1_standardCourseMember';

  if (courseSlugError || slugError || projectError) {
    Sentry.captureException(courseSlugError ?? slugError ?? projectError);
  }

  return (
    <MiniModalRedirectContext.Provider
      value={{
        isLoading: isCourseSlugLoading || isSlugLoading || isProjectLoading,
        courseSlug: course?.slug,
        s12nSlug: s12n?.slug,
        s12nProductVariant: s12n?.productVariant,
        isProject: !!isProject,
      }}
    >
      {children}
    </MiniModalRedirectContext.Provider>
  );
};

export default MiniModalRedirectProvider;
